import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DamageEntry = () => {
  const [items, setItems] = useState([]);
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    damage_number: `DMG-${Date.now()}`,
    damage_date: new Date().toISOString().split('T')[0],
    item_id: '',
    item_name: '',
    quantity: 1,
    unit_price: 0,
    total_loss: 0,
    damage_reason: '',
    responsible_person: '',
    action_taken: '',
    reported_by: 'Admin',
    status: 'Reported'
  });

  useEffect(() => {
    fetchItems();
    fetchDamages();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await apiClient.get('/api/inventory/items');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setItems(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchDamages = async () => {
    try {
      const response = await apiClient.get('/api/inventory/damaged-items');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setDamages(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleItemChange = (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      setFormData({
        ...formData,
        item_id: itemId,
        item_name: item.item_name,
        unit_price: item.unit_price,
        total_loss: formData.quantity * item.unit_price
      });
    }
  };

  const handleQuantityChange = (quantity) => {
    setFormData({
      ...formData,
      quantity: quantity,
      total_loss: quantity * formData.unit_price
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/api/inventory/damaged-items', formData);
      if (response.status === 200 || response.data) {
        alert('Damage reported successfully!');
        fetchDamages();
        fetchItems();
        setFormData({
          damage_number: `DMG-${Date.now()}`,
          damage_date: new Date().toISOString().split('T')[0],
          item_id: '',
          item_name: '',
          quantity: 1,
          unit_price: 0,
          total_loss: 0,
          damage_reason: '',
          responsible_person: '',
          action_taken: '',
          reported_by: 'Admin',
          status: 'Reported'
        });
      } else {
        alert('Failed to report damage');
      }
    } catch (error) {
      alert('Failed to report damage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Report Damaged Item</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Damage Number</Label>
                <Input value={formData.damage_number} disabled />
              </div>
              <div>
                <Label>Damage Date *</Label>
                <Input type="date" required value={formData.damage_date} onChange={(e) => setFormData({ ...formData, damage_date: e.target.value })} />
              </div>
              <div>
                <Label>Reported By</Label>
                <Input value={formData.reported_by} onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item *</Label>
                <Select value={formData.item_id} onValueChange={handleItemChange}>
                  <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.item_name} (Stock: {item.quantity})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity *</Label>
                <Input type="number" min="1" required value={formData.quantity} onChange={(e) => handleQuantityChange(parseInt(e.target.value))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price</Label>
                <Input value={formData.unit_price} disabled />
              </div>
              <div>
                <Label>Total Loss</Label>
                <Input value={formData.total_loss} disabled />
              </div>
            </div>
            <div>
              <Label>Damage Reason *</Label>
              <Input required value={formData.damage_reason} onChange={(e) => setFormData({ ...formData, damage_reason: e.target.value })} placeholder="Explain the damage" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Responsible Person</Label>
                <Input value={formData.responsible_person} onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })} placeholder="If applicable" />
              </div>
              <div>
                <Label>Action Taken</Label>
                <Input value={formData.action_taken} onChange={(e) => setFormData({ ...formData, action_taken: e.target.value })} placeholder="Corrective action" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full">{loading ? 'Submitting...' : 'Report Damage'}</Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Damaged Items History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Damage #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Item</th>
                  <th className="text-left p-3">Quantity</th>
                  <th className="text-left p-3">Loss</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {damages.length === 0 ? (
                  <tr><td colSpan="7" className="text-center p-4 text-gray-500">No damaged items reported.</td></tr>
                ) : (
                  damages.map((damage) => (
                    <tr key={damage.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{damage.damage_number}</td>
                      <td className="p-3">{formatDate(damage.damage_date)}</td>
                      <td className="p-3">{damage.item_name}</td>
                      <td className="p-3">{damage.quantity}</td>
                      <td className="p-3 text-red-600 font-medium">₹{damage.total_loss}</td>
                      <td className="p-3">{damage.damage_reason}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          damage.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                          damage.status === 'Under Investigation' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{damage.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DamageEntry;